import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { MY_DRAWN_AREA, MY_TRAVEL_TIME_AREA } from '@propertypal/shared/src/constants/search';
import useRecentSearches from '@propertypal/shared/src/hooks/useRecentSearches';
import useSearchState from '@propertypal/shared/src/hooks/useSearchState';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import platform from '@propertypal/shared/src/utils/platform';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import SearchInput from '@propertypal/web-ui/src/inputs/SearchInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import { useRouter } from 'next/router';
import React, { CSSProperties, FormEvent, FunctionComponent, useRef, useState } from 'react';
import getCurrentLocation from '../../services/location';
import { Container, SearchButtons, SearchForm, SearchInputContainer, SearchText } from './HomeSearchBar.style';
import SearchPreviewModal from './SearchPreviewModal';

interface Props {
  containerStyle?: CSSProperties;
  onSubmit: (baseUrl: string, searchText: string, filters: Filters) => void;
  extraNlpParams?: string;
  placeholder?: string;
  showTravelPreview?: boolean;
  showSaleTypeOptions?: boolean;
  loading?: boolean;
  selectCategory?: string;
  setSelectedCategory?: (category: string) => void;
}

const SALE_TYPES = [
  { label: 'Sales', value: 'pt=residential&st=sale' },
  { label: 'Rentals', value: 'pt=residential&st=rent' },
  { label: 'Student Rentals', value: 'pt=student&st=rent' },
  { label: 'Commercial Sale', value: 'pt=commercial&st=sale' },
  { label: 'Commercial Rent', value: 'pt=commercial&st=rent' },
  { label: 'New Homes', value: 'pt=newhomes&st=sale' },
];

const HomeSearchBar: FunctionComponent<Props> = (props) => {
  const { recentSearches, removeRecentSearch } = useRecentSearches();
  const { handleChangeText, nlpData, searchText, getLocation, fetchingLocation } = useSearchState(props.extraNlpParams);
  const [showSearchPreview, setShowSearchPreview] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const handleLocationClick = (position: 'suggestion' | 'inline') => {
    trackGaEvent('current_location_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    return getLocation(getCurrentLocation, true);
  };

  const handleTravelTimeClick = (position: 'suggestion' | 'inline') => {
    trackGaEvent('travel_time_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    setShowSearchPreview('travelTime');
  };

  const handleDrawSearchClick = (position: 'suggestion' | 'inline') => {
    trackGaEvent('draw_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    setShowSearchPreview('drawSearch');
  };

  const getLocationText = async () => {
    const position = await getLocation(getCurrentLocation, false);

    if (position.lat) {
      return position;
    }

    return {};
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const position = await getLocationText();

    props.onSubmit('', searchText, {
      ...position,
      category: 'residential',
      saleType: 'sale',
    });
  };

  const handleSuggestionClick = (token: Suggestion) => {
    if (token.url) {
      router.push(token.url);
    } else {
      handleChangeText(token.value);
    }
  };

  const handlePreviewSubmit = (polyIds: number[], category: string, saleType: string) => {
    props.onSubmit('/map', showSearchPreview === 'travelTime' ? MY_TRAVEL_TIME_AREA : MY_DRAWN_AREA, {
      category,
      saleType,
      polyIds,
    });
  };

  return (
    <Container style={props.containerStyle}>
      <SearchForm action="" onSubmit={handleSubmit}>
        <SearchInputContainer>
          <SearchInput
            containerStyle={{ borderRadius: platform.select(20, 6) }}
            inputRef={inputRef}
            disableSavedSearches
            onChangeText={handleChangeText}
            loading={fetchingLocation}
            placeholder={props.placeholder}
            suggestionsOffset={74}
            suggestionsLeftOffset={6}
            value={searchText}
            nlpData={nlpData}
            onLocationClick={handleLocationClick}
            onSuggestionClick={handleSuggestionClick}
            onTravelTimeClick={props.showTravelPreview ? handleTravelTimeClick : undefined}
            onDrawSearchClick={props.showTravelPreview ? handleDrawSearchClick : undefined}
            isHomePage
            recentSearches={recentSearches}
            onRecentSearchClick={(s, f) => props.onSubmit('', s, f)}
            onRemoveRecentSearch={removeRecentSearch}
          />
        </SearchInputContainer>

        <SearchButtons multiButtons={props.showSaleTypeOptions || !!props.selectCategory}>
          {props.selectCategory && props.setSelectedCategory && (
            <SelectInput
              ariaLabel="search category"
              id="categorySelect"
              options={SALE_TYPES}
              value={props.selectCategory}
              onChange={props.setSelectedCategory}
            />
          )}

          <Button
            containerStyle={{ height: '54px', padding: 0 }}
            loading={props.loading}
            testID="forSaleButton"
            type="submit"
          >
            <SearchText>
              <span>Search</span>
              <FontAwesomeIcon icon={faSearch} />
            </SearchText>
          </Button>
        </SearchButtons>
      </SearchForm>

      {showSearchPreview && (
        <SearchPreviewModal
          show={showSearchPreview}
          onClose={() => setShowSearchPreview('')}
          onSubmit={handlePreviewSubmit}
          showSaleTypeOptions={props.showSaleTypeOptions}
        />
      )}
    </Container>
  );
};

export default HomeSearchBar;
